import React, { createContext, useContext, useState, useCallback, useRef, ReactNode } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { buildApiUrl, API_ENDPOINTS } from '../config/api';
import { useAuth } from './AuthContext';
import webChatHistoryService from '../services/webChatHistoryService';
import { ErrorHandler } from '../utils/errorHandler';

// Types - shared with RISAssistant
// RISMode is kept for backward compatibility with old sessions
// But the app now only uses 'tooluse' mode (RISearch 2.0)
export type RISMode = 'general' | 'deep-search' | 'tooluse';

export interface EnhancedSource {
  type: 'paragraph' | 'judgment' | 'document';
  reference: string;
  title: string;
  risLink?: string;
  found: boolean;
  documentId?: string;
}

export interface DeepRISearchCase {
  courtDecision: string;
  principle: string;
  reasoning: string;
  relevanceScore: number;
  link: string;
  norms: string[];
}

export interface DeepRISearchJurisprudence {
  summary: string;
  cases: DeepRISearchCase[];
}

export interface DeepRISearchResponse {
  answer: string;
  jurisprudence?: DeepRISearchJurisprudence;
  sources: string[];
  metadata: {
    processingTime: number;
    baseDecision: {
      spezifizierung: boolean;
      judikatur: boolean;
    };
    jurisprudenceStats?: {
      searchResults: number;
      extractedCases: number;
      relevantCases: number;
      extractedUrteile?: number;
    };
  };
}

export interface RISLinkMetadata {
  geschaeftszahlen: Array<{
    geschaeftszahl: string;
    risUrl: string;
  }>;
  paragraphen: Array<{
    paragraph: string;
    risLink: string;
  }>;
}

export interface CaseLawDecision {
  rsNumber?: string;
  date?: string;
  court: string;
  senate?: string;
  significance: 'Leitentscheidung' | 'Grundsatzentscheidung' | 'Stützend' | 'Wendepunkt';
  risLink?: string;
  coreRuling: string;
  strategicRelevance: number;
  directApplicability: string;
  argumentationPower: number;
  confidenceScore: number;
  category: 'LEITENTSCHEIDUNG' | 'STÜTZEND' | 'INNOVATIV';
}

export interface StructuredCaseLawResponse {
  executiveSummary: {
    successProbability: string;
    strategicAssessment: string;
    keyStrengths: string[];
    potentialWeaknesses: string[];
  };
  primaryJurisprudence: CaseLawDecision[];
  supportingJurisprudence: CaseLawDecision[];
  innovativeApproaches: CaseLawDecision[];
  jurisprudenceTrend: {
    developmentDirection: string;
    temporalWeighting: string;
    senateSpecificTendencies: string;
  };
  readyToUseTextBlocks: Array<{
    type: 'OPENING' | 'SUPPORTING' | 'CLOSING';
    text: string;
    supportingRsNumbers: string[];
  }>;
  qualityMetrics: {
    totalRelevantDecisions: number;
    averageConfidenceScore: number;
    jurisprudenceConsistency: 'HIGH' | 'MEDIUM' | 'LOW';
    argumentationStrength: 'VERY_STRONG' | 'STRONG' | 'MODERATE' | 'WEAK';
  };
}

export interface RISMessage {
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
  mode?: RISMode;
  sources?: string[];
  structuredData?: StructuredCaseLawResponse;
  verifiedSources?: EnhancedSource[];
  sourceVerificationStatus?: 'pending' | 'completed' | 'failed';
  messageId?: string;
  deepRISearchData?: DeepRISearchResponse;
  risLinkMetadata?: RISLinkMetadata;
}

// Pending search state
interface PendingSearch {
  id: string;
  query: string;
  mode: RISMode;
  startedAt: Date;
  sessionId: string;
}

// Search result
interface SearchResult {
  userMessage: RISMessage;
  assistantMessage: RISMessage;
  sessionId: string;
}

// Maximum concurrent searches allowed
const MAX_CONCURRENT_SEARCHES = 5;

// Context type
interface RISearchContextType {
  // State
  pendingSearches: PendingSearch[];
  pendingResult: SearchResult | null;
  isSearching: boolean;
  activeSearchCount: number;
  searchError: string | null;

  // Actions
  startSearch: (
    query: string,
    mode: RISMode,
    sessionId: string,
    existingMessages: RISMessage[],
    currentChatSessionId: string | null
  ) => Promise<SearchResult | null>;  // Now returns the result directly
  cancelSearch: () => void;
  consumeResult: () => SearchResult | null;
  isSessionSearching: (sessionId: string) => boolean;
  clearPendingSearch: () => void;
  clearError: () => void;
}

const RISearchContext = createContext<RISearchContextType | undefined>(undefined);

interface RISearchProviderProps {
  children: ReactNode;
}

export const RISearchProvider: React.FC<RISearchProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [pendingSearches, setPendingSearches] = useState<PendingSearch[]>([]);
  const [pendingResult, setPendingResult] = useState<SearchResult | null>(null);
  const [searchError, setSearchError] = useState<string | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // Derived state
  const isSearching = pendingSearches.length > 0;
  const activeSearchCount = pendingSearches.length;

  // Helper to check if a specific session has an active search
  const isSessionSearching = useCallback((sessionId: string): boolean => {
    return pendingSearches.some(search => search.sessionId === sessionId);
  }, [pendingSearches]);

  // Helper to generate AI title for session
  const generateSessionTitle = async (sessionId: string, query: string): Promise<void> => {
    try {
      console.log(`[RISearchContext] Generating AI title for session ${sessionId}...`);

      const response = await axios.post(
        buildApiUrl(`/api/ris/sessions/${sessionId}/generate-title`),
        { query },
        { withCredentials: true }
      );

      if (response.data.success && response.data.title) {
        console.log(`[RISearchContext] AI title generated: "${response.data.title}"`);
        // Dispatch event to notify sidebar to refresh
        window.dispatchEvent(new CustomEvent('session:titleUpdated', {
          detail: { sessionId, title: response.data.title }
        }));
      }
    } catch (error) {
      console.error('[RISearchContext] Failed to generate AI title:', error);
      // Non-fatal - session will keep the temporary title
    }
  };

  // Helper to save chat to history
  // Note: Session should already be created by startSearch() for new sessions
  const saveRISChatToHistory = async (
    userMessage: RISMessage,
    assistantMessage: RISMessage,
    sessionId: string,
    currentChatSessionId: string | null,
    selectedMode: RISMode
  ): Promise<string | null> => {
    try {
      console.log(`[RISearchContext] Saving messages to history...`);

      const chatSessionId = currentChatSessionId || sessionId;

      // User message already saved early (before API call) - skip to avoid duplicate

      // Save assistant message
      await webChatHistoryService.saveRISAssistantMessage(
        chatSessionId,
        assistantMessage.content,
        assistantMessage.messageId,
        assistantMessage.sources,
        assistantMessage.sourceVerificationStatus || 'completed'
      );

      return chatSessionId;
    } catch (error) {
      console.error('[RISearchContext] Error saving to history:', error);
      return null;
    }
  };

  const startSearch = useCallback(async (
    query: string,
    mode: RISMode,
    sessionId: string,
    existingMessages: RISMessage[],
    currentChatSessionId: string | null
  ): Promise<SearchResult | null> => {
    // Check rate limit
    if (pendingSearches.length >= MAX_CONCURRENT_SEARCHES) {
      toast.error('Maximale Anzahl paralleler Suchen erreicht (5). Bitte warten Sie.');
      return null;
    }

    // Clear previous results and errors
    setPendingResult(null);
    setSearchError(null);

    const searchId = `${Date.now()}_${Math.random().toString(36).substring(2, 8)}`;
    const queryTimestamp = new Date(); // Capture timestamp at query submission

    const userMessage: RISMessage = {
      role: 'user',
      content: query,
      timestamp: queryTimestamp,
      mode: mode,
      messageId: `${user?.id}_${Date.now()}_${Math.random().toString(36).substring(2, 8)}`
    };

    // Add search to pending array
    const newSearch: PendingSearch = {
      id: searchId,
      query,
      mode,
      startedAt: queryTimestamp,
      sessionId
    };
    setPendingSearches(prev => [...prev, newSearch]);

    // Determine if this is a new session (no existing messages)
    const isNewSession = existingMessages.length === 0;

    // IMMEDIATELY create session if new, so it appears in sidebar right away
    if (isNewSession && sessionId) {
      try {
        // Generate a smart temp title from the query (first 50 chars)
        const tempTitle = query.length > 50
          ? query.substring(0, 47) + '...'
          : query;

        console.log(`[RISearchContext] Creating session immediately: ${sessionId}`);

        await webChatHistoryService.createChatSessionV2({
          sessionId: sessionId,
          title: tempTitle,
          serviceType: 'ris'
        });

        // Dispatch event to notify sidebar IMMEDIATELY
        window.dispatchEvent(new CustomEvent('session:created', {
          detail: { sessionId: sessionId, title: tempTitle }
        }));

        // Generate AI title asynchronously (don't block the search)
        generateSessionTitle(sessionId, query);

      } catch (sessionError: any) {
        // Session might already exist (409/400) - that's OK, continue with search
        if (sessionError.response?.status !== 400 && sessionError.response?.status !== 409) {
          console.warn('[RISearchContext] Failed to create session:', sessionError);
        }
      }
    }

    try {
      // Save user message to DB immediately (before search API call)
      // so it's available if user switches sessions mid-search
      try {
        await webChatHistoryService.saveRISUserMessage(
          sessionId, userMessage.content, userMessage.messageId
        );
        console.log(`[RISearchContext] User message saved to DB early`);
      } catch (earlyErr) {
        console.warn('[RISearchContext] Failed to save user message early:', earlyErr);
      }

      // Prepare chat history for context
      const chatHistory = existingMessages.map(msg => ({
        role: msg.role === 'assistant' ? 'ris' : msg.role,
        content: msg.content,
        timestamp: msg.timestamp
      }));

      console.log(`[RISearchContext] Starting RISearch 2.0 search...`);

      // Create AbortController for cancellation support
      abortControllerRef.current = new AbortController();

      // Make API request - always use tooluse (RISearch 2.0)
      // Cookies are sent automatically with withCredentials: true
      const response = await axios.post(buildApiUrl(API_ENDPOINTS.RIS_TOOLUSE), {
        userQuery: query,
        mode: 'tooluse',
        sessionId: sessionId,
        userId: user?.id,
        chatHistory: chatHistory
      }, { withCredentials: true, signal: abortControllerRef.current.signal });

      // Backend returns { data: {...} } structure - extract safely
      const responseData = response.data?.data || response.data;

      if (!responseData?.answer) {
        throw new Error('Invalid response: missing answer field');
      }

      const assistantMessage: RISMessage = {
        role: 'assistant',
        content: responseData.answer,
        timestamp: new Date(),
        mode: mode,
        sources: responseData.sources || [],
        structuredData: responseData.structuredData,
        sourceVerificationStatus: responseData.sourceVerificationStatus,
        verifiedSources: responseData.verifiedSources,
        messageId: responseData.messageId,
        deepRISearchData: mode === 'deep-search' ? {
          answer: responseData.answer,
          jurisprudence: responseData.jurisprudence,
          sources: responseData.sources || [],
          metadata: responseData.metadata || {
            processingTime: 0,
            baseDecision: { spezifizierung: false, judikatur: false }
          }
        } : undefined,
        risLinkMetadata: responseData.risLinkMetadata
      };

      // Save to chat history
      await saveRISChatToHistory(
        userMessage,
        assistantMessage,
        sessionId,
        currentChatSessionId,
        mode
      );

      console.log(`[RISearchContext] Search completed successfully`);

      // Store result
      const result: SearchResult = {
        userMessage,
        assistantMessage,
        sessionId
      };

      setPendingResult(result);
      // Remove this search from pending array
      setPendingSearches(prev => prev.filter(s => s.id !== searchId));

      // Show toast notification with navigation
      const queryPreview = query.substring(0, 40) + (query.length > 40 ? '...' : '');

      toast.success('RISearch - Suche abgeschlossen', {
        description: queryPreview,
        action: {
          label: 'Anzeigen',
          onClick: () => {
            // Navigation will be handled by RISAssistant when it mounts
            window.location.hash = '#/ris';
          }
        },
        duration: 10000, // 10 seconds
      });

      // Return result directly so caller can use it without waiting for state update
      return result;

    } catch (error: any) {
      // Don't show error for user-initiated cancellations
      if (axios.isCancel(error)) {
        console.log('[RISearchContext] Search cancelled by user');
        setPendingSearches(prev => prev.filter(s => s.id !== searchId));
        return null;
      }

      console.error('[RISearchContext] Search error:', error);
      const errorMessage = ErrorHandler.handleApiError(error);
      setSearchError(errorMessage);
      // Remove this search from pending array on error
      setPendingSearches(prev => prev.filter(s => s.id !== searchId));

      // Show error toast
      toast.error('RISearch - Suche fehlgeschlagen', {
        description: errorMessage,
        duration: 8000,
      });

      return null;
    } finally {
      abortControllerRef.current = null;
    }
  }, [user?.id, pendingSearches.length]);

  const consumeResult = useCallback((): SearchResult | null => {
    const result = pendingResult;
    setPendingResult(null);
    return result;
  }, [pendingResult]);

  const cancelSearch = useCallback(() => {
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
      abortControllerRef.current = null;
    }
    setPendingSearches([]);
    setSearchError(null);
  }, []);

  const clearPendingSearch = useCallback(() => {
    setPendingSearches([]);
    setPendingResult(null);
    setSearchError(null);
  }, []);

  const clearError = useCallback(() => {
    setSearchError(null);
  }, []);

  const value: RISearchContextType = {
    pendingSearches,
    pendingResult,
    isSearching,
    activeSearchCount,
    searchError,
    startSearch,
    cancelSearch,
    consumeResult,
    isSessionSearching,
    clearPendingSearch,
    clearError,
  };

  return (
    <RISearchContext.Provider value={value}>
      {children}
    </RISearchContext.Provider>
  );
};

export const useRISearch = (): RISearchContextType => {
  const context = useContext(RISearchContext);
  if (context === undefined) {
    throw new Error('useRISearch must be used within a RISearchProvider');
  }
  return context;
};
